import { Component, Inject } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Action, ActionsDataService, IActionType } from '@services/actions-data.service';
import { AssetsDataService, LevelAsset } from '@services/assets-data.service';
import { RuleListDialogData } from '../rule-list/rule-list.component';
import { RulesDataService } from '@services/rules-data.service';
import { GlobalFunctions } from '@global/GlobalFunctions';
import { combineLatest, distinctUntilChanged, filter, finalize, Subject, takeUntil } from 'rxjs';
import { Router } from '@angular/router';
import { convertEpochToDateString } from '@functions/functions';
import { WebAppUser } from '@services/authentication.service';

export class ActionListDialogData {
	asset: LevelAsset;
	accountGUID: string;
	organisationUID: string;
	currentUser?: WebAppUser;
}

@Component({
	selector: 'app-asset-actions',
	templateUrl: './asset-actions.component.html',
	styleUrls: ['./asset-actions.component.scss']
})
export class AssetActionsComponent {

	/** accountGUID */
	public accountGUID: string;

	/** organisationUID */
	public organisationUID: string

	/** Asset */
	public Asset: LevelAsset;

	/** Actions */
	public Actions: Action[] = [];

	/** filteredActions */
	public filteredActions: Action[] = [];

	/** actionTypes - values correspond to value in db table */
	public actionTypes: IActionType[] = [];

	/** now */
	public now: number = new Date().getTime();

	/** myActionsOnly */
	public myActionsOnly: boolean = false;

	/** overdueOnly */
	public overdueOnly: boolean = false;

	/** currentUser */
	public currentUser: WebAppUser;

	/**
	 * 
	 * @param fb 
	 * @param dialogRef 
	 * @param dialogData 
	 * @param assetsDataService 
	 * @param actionsDataService 
	 * @param rulesDataService 
	 * @param globalFunctions 
	 * @param router 
	 */
	constructor(
		private fb: FormBuilder,
		public dialogRef: MatDialogRef<AssetActionsComponent>,
		@Inject(MAT_DIALOG_DATA) public dialogData: ActionListDialogData,
		private assetsDataService: AssetsDataService,
		private actionsDataService: ActionsDataService,
		private rulesDataService: RulesDataService,
		private globalFunctions: GlobalFunctions,
		private router: Router
	) {
		this.Asset = this.dialogData.asset;
		this.accountGUID = this.dialogData.accountGUID;
		this.organisationUID = this.dialogData.organisationUID;
		if (this.dialogData.currentUser) {
			this.currentUser = this.dialogData.currentUser;
		}

		this.globalFunctions.displaySpinner(true);
		this.getActions(this.Asset);
		this.globalFunctions.displaySpinner(false);
	}

	/** getRules */
	getActions(asset: LevelAsset) {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();
		combineLatest([
			this.actionsDataService.getActions$(this.accountGUID, this.organisationUID, 0, asset.AssetID),
			this.actionsDataService.getActionTypes$()
		]).pipe(
			takeUntil(fetchDataSubject), distinctUntilChanged(),
			filter(([asset, ActionTypes]) => !!asset && !!ActionTypes),
			finalize(() => {
				// Ensure the subscription is terminated and the subject is completed
				fetchDataSubject.next();
				fetchDataSubject.complete();
			})
		).subscribe({
			next: ([actions, ActionTypes]) => {
				this.actionTypes = ActionTypes;
				this.Actions = actions.filter((x: Action) => x.Completed == false);
				this.filteredActions = this.Actions;
				this.filterActions();
			},
			error: (error) => { this.globalFunctions.HandleError(error, "Error: this.actionsDataService.getActions$", false); },
			//complete: () => { console.log("this.siteSubscriptions.selectedOrganisationUID$.subscribe: Complete"); }
		});
	}

	/**
	 * Cancel button for dialog
	 */
	onCancelClick(): void {
		this.dialogRef.close();
	}

	/**
	 * getActionTypeFromValue
	 * @param value value 
	 * @returns 
	 */
	getActionTypeFromValue(value: number): string {
		let result = this.actionTypes.find((x: IActionType) => x.ActionTypeID == value)?.Description;
		if (result) {
			return result;
		} else {
			return "";
		}
	}

	/**
	 * convertEpoch
	 * @param epoch
	 * @returns
	 */
	convertEpoch(epoch: number): string {
		/** Use the Global Function */
		return convertEpochToDateString(epoch);
	}

	/**
	 * goToAction
	 * @param event event  
	 * @param action action 
	 */
	goToAction(event: MouseEvent, action: Action) {
		event.stopPropagation();
		this.dialogRef.close();
		this.router.navigate([`/${this.accountGUID}/organisation/${this.organisationUID}/actions/${action.ActionID}`]);
	}

	/**
	 * convertEpochNoTime
	 * @param epoch epoch 
	 */
	convertEpochNoTime(epoch: number): string {
		let arr = convertEpochToDateString(epoch).split(" ");
		arr.pop();
		let result: string = "";
		arr.forEach(item => {
			result = result.concat(item, " ");
		})
		return result;
	}

	/**
	 * checkIfOverdue
	 * @param action action 
	 * @returns 
	 */
	checkIfOverdue(action: Action): boolean {
		let result: boolean = false;
		if (action.TargetCompletionTimestamp != undefined && (action.TargetCompletionTimestamp + (1 * 24 * 60 * 60 * 1000)) < this.now) {
			if (!action.Completed) {
				result = true;
			}
		}
		return result;
	}

	/**
	 * toggleCheckbox
	 * @param event event 
	 * @param overdue overdue 
	 */
	toggleCheckbox(event: any, overdue: boolean) {
		if (overdue) {
			this.overdueOnly = event.checked;
		} else {
			this.myActionsOnly = event.checked;
		}
		this.filterActions();
	}

	/**
	 * filterActions
	 */
	filterActions() {
		this.filteredActions = this.Actions.sort((a, b) => (a.TargetCompletionTimestamp ?? 0) - (b.TargetCompletionTimestamp ?? 0));
		if (this.myActionsOnly) {
			this.filteredActions = this.filteredActions.filter((x: Action) => x.AssignedToUserSub == this.currentUser.Sub);
		}
		if (this.overdueOnly) {
			this.filteredActions = this.filteredActions.filter((x: Action) => this.checkIfOverdue(x));
		}
	}
}
